import React, { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { aiEnhanceDescription, AIEnhanceFeature } from "@/utils/aiEnhanceService";

const AIEnhancementSandbox: React.FC = () => {
  const [projectDescription, setProjectDescription] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [result, setResult] = useState<{
    enhancedDescription: string;
    features: AIEnhanceFeature[];
    error: string | null;
  } | null>(null);

  // Function to enhance the project description
  const enhanceDescription = async () => {
    if (!projectDescription.trim()) {
      alert("Please enter a project description");
      return;
    }

    setIsLoading(true);
    
    try {
      const enhancementResult = await aiEnhanceDescription(projectDescription);
      
      if (enhancementResult.error) {
        setResult({
          enhancedDescription: '',
          features: [],
          error: enhancementResult.error
        });
      } else {
        setResult(enhancementResult);
      }
    } catch (error) {
      setResult({
        enhancedDescription: '',
        features: [],
        error: error instanceof Error ? error.message : 'An unknown error occurred'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Remove a feature from the list
  const removeFeature = (index: number) => {
    if (!result) return;
    
    const updatedFeatures = [...result.features];
    updatedFeatures.splice(index, 1);
    setResult({
      ...result,
      features: updatedFeatures
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#030308]">
      <Header />
      <div className="flex-grow container mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-6 text-white">AI Enhancement Sandbox</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl font-semibold mb-4 text-white">Input</h2>
            <Textarea
              placeholder="Enter your project description here..."
              className="w-full h-64 bg-[#0a0a1a] border-white/10 text-white"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
            />
            <Button
              onClick={enhanceDescription}
              disabled={isLoading}
              className="mt-4 bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90 text-white"
            >
              {isLoading ? "Processing..." : "AI Enhance Description"}
            </Button>
          </div>
          
          <div>
            <h2 className="text-xl font-semibold mb-4 text-white">Result</h2>
            {result?.error && (
              <div className="text-red-500 mb-4">Error: {result.error}</div>
            )}
            
            {result && !result.error && (
              <>
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-2 text-white">Enhanced Description:</h3>
                  <div className="bg-[#0a0a1a] border border-white/10 rounded-md p-4 text-white">
                    {result.enhancedDescription}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-2 text-white">Features (click X to remove):</h3>
                  <div className="space-y-2">
                    {result.features.map((feature, index) => (
                      <Card key={index} className="bg-[#0a0a1a] border-white/10 p-3 text-white flex justify-between items-start">
                        <div>
                          <div className="font-medium">{feature.name} {feature.isCore && <span className="text-green-400">(Core)</span>}</div>
                          <div className="text-white/70">{feature.description}</div>
                          <div className="text-xs mt-1">
                            Impact: <span className={`${
                              feature.impact === 'high' ? 'text-green-400' : 
                              feature.impact === 'medium' ? 'text-yellow-400' : 
                              'text-blue-400'
                            }`}>{feature.impact}</span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFeature(index)}
                          className="text-white/70 hover:text-white hover:bg-transparent"
                        >
                          <X size={16} />
                        </Button>
                      </Card>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AIEnhancementSandbox;
